import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { TreeModule } from 'primeng/tree';
import { TreeNode } from 'primeng/api';
import { TreeNodeSelectEvent, TreeNodeUnSelectEvent } from 'primeng/types/tree';
import { Stage, StageTreeNode } from '@cosmos/shared';

@Component({
  selector: 'cosmos-move-stage-dialog',
  templateUrl: './move-stage-dialog.component.html',
  imports: [TranslatePipe, ButtonModule, MessageModule, TooltipModule, TreeModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MoveStageDialogComponent {
  private readonly ref = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);

  readonly movingStage: Stage = this.config.data.stage;
  readonly movingStageId: string = this.movingStage._id;
  // The project's full root-to-leaf stage tree(s), same shape every other
  // tree picker in this module consumes.
  readonly stageTree: StageTreeNode[] = this.config.data.stageTree;

  readonly currentParentStageId: string | null =
    (this.movingStage.parentStage as Stage)?._id ?? (this.movingStage.parentStage as string) ?? null;

  readonly treeNodes: TreeNode[] = this.buildTreeWithRoot(this.stageTree);

  private readonly nodesById = this.buildNodeIndex(this.treeNodes);
  private readonly stageNamesById = this.buildNameIndex(this.stageTree);

  private readonly currentNode: TreeNode | null =
    this.currentParentStageId === null
      ? (this.nodesById.get('root') ?? null)
      : (this.nodesById.get(this.currentParentStageId) ?? null);

  // Same "always exactly one selection, no valid empty state" pattern as
  // MeasurementChangeStageDialogComponent — see that component's comments
  // for the full rationale (PrimeNG single-select toggles off on a second
  // click of the same node; there's no valid "no target" state here either).
  readonly selectedNode = signal<TreeNode | null>(this.currentNode);

  readonly selectedTargetId = computed(() => (this.selectedNode()?.data?.id ?? null) as string | null);

  readonly currentParentName = computed(() =>
    this.currentParentStageId === null ? null : this.stageNamesById.get(this.currentParentStageId),
  );
  readonly selectedTargetName = computed(() => {
    const id = this.selectedTargetId();
    return id === null ? null : this.stageNamesById.get(id);
  });
  readonly selectedIsRoot = computed(() => this.selectedTargetId() === null);
  readonly hasChanged = computed(() => this.selectedTargetId() !== this.currentParentStageId);

  // Builds the real stage tree, then prepends a synthetic "(Project root)"
  // node ahead of it — always selectable, since moving to root can never
  // create a cycle. This is the only way to express "move to root level" in
  // a tree that otherwise only contains real stages.
  private buildTreeWithRoot(stages: StageTreeNode[]): TreeNode[] {
    const rootNode: TreeNode = {
      label: '',
      data: { type: 'root', id: null },
      selectable: true,
      icon: 'fa-solid fa-house',
      key: 'root',
    };
    return [rootNode, ...this.buildTree(stages)];
  }

  // Only stage nodes are ever in this tree — no measurement leaves, since
  // the picker's job is choosing a destination stage. The stage being moved,
  // and every one of its own descendants, are marked unselectable: moving a
  // stage into itself or into any of its descendants would create a cycle,
  // and the backend enforces this too (defense in depth, not a substitute).
  private buildTree(stages: StageTreeNode[], insideMovingSubtree = false): TreeNode[] {
    return stages.map((s) => {
      const isMovingSubtree = insideMovingSubtree || s._id === this.movingStageId;
      return {
        label: s.name,
        data: { type: 'stage', id: s._id },
        children: this.buildTree(s.children, isMovingSubtree),
        icon: 'fa-solid fa-folder',
        selectable: !isMovingSubtree,
      };
    });
  }

  private buildNameIndex(stages: StageTreeNode[], index = new Map<string, string>()): Map<string, string> {
    for (const s of stages) {
      index.set(s._id, s.name);
      this.buildNameIndex(s.children, index);
    }
    return index;
  }

  private buildNodeIndex(nodes: TreeNode[], index = new Map<string, TreeNode>()): Map<string, TreeNode> {
    for (const n of nodes) {
      index.set(n.data.id ?? 'root', n);
      this.buildNodeIndex(n.children ?? [], index);
    }
    return index;
  }

  onNodeSelect(event: TreeNodeSelectEvent): void {
    if (event.node.selectable === false) return;
    this.selectedNode.set(event.node);
  }

  // Falls back to the stage's current parent (or root node) — see
  // selectedNode's comment. Deferred to a microtask for the same reason
  // documented in MeasurementChangeStageDialogComponent.onNodeUnselect:
  // pushing the fallback synchronously, inside the same pass PrimeNG uses to
  // apply its own unselected state, gets absorbed rather than re-rendering.
  onNodeUnselect(event: TreeNodeUnSelectEvent): void {
    queueMicrotask(() => this.selectedNode.set(this.currentNode ? { ...this.currentNode } : null));
  }

  confirm(): void {
    this.ref.close({ parentStage: this.selectedTargetId() });
  }

  cancel(): void {
    this.ref.close(undefined);
  }
}
